import { useState, useEffect } from "react";
import { ConversationPanel } from "@/components/sms/conversation-panel";
import { ConversationsSidebar } from "@/components/sms/conversations-sidebar";
import { DataService } from "@/services/data";
import { useSms } from "@/providers/sms";
import { type PaginatedResponse } from "@/types/common";
import { type ConversationDetail, type Conversation, type Task, type SmsCredits } from "@/types/crm/sms";

interface SMSConversationsProps {
    isOpen: boolean;
}

export function SMSConversations({ isOpen }: SMSConversationsProps) {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [messages, setMessages] = useState<Task[]>([]);
    const [nextUrl, setNextUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [selectedConversationDetail, setSelectedConversationDetail] = useState<ConversationDetail | null>(null);
    const [smsCredits, setSmsCredits] = useState<SmsCredits | null>(null);
    const [isLoadingSmsCredits, setIsLoadingSmsCredits] = useState<boolean>(false);
    const [loadedPagesCount, setLoadedPagesCount] = useState<number>(0);
    const { focusedConversationId, setFocusedConversationId, setIsOpen, prefetchedConversation, setPrefetchedConversation } = useSms();

    useEffect(() => {
        if (!isOpen) return;
        // Reset state when opening
        setConversations([]);
        setMessages([]);
        setNextUrl(null);
        setSelectedConversationDetail(null);
        setSmsCredits(null);
        setIsLoadingSmsCredits(true);
        setIsLoading(true);
        setLoadedPagesCount(0);

        DataService.client
            .url("/crm/app/conversations/")
            .get()
            .json<PaginatedResponse<Conversation>>()
            .then(async data => {
                setConversations(data.results);
                setNextUrl(data?.next ?? null);
                setLoadedPagesCount(1);
                if (prefetchedConversation) {
                    // Prefer prefetched conversation (from openFromPhoneNumber)
                    setSelectedConversationDetail(prefetchedConversation);
                    setConversations(prev => {
                        const exists = prev.some(c => c.id === prefetchedConversation.id);
                        return exists ? prev : [prefetchedConversation as unknown as Conversation, ...prev];
                    });
                    // Clear focus to avoid duplicate fetch effect and clear the prefetch
                    setFocusedConversationId(null);
                    setPrefetchedConversation(null);
                } else if (data.results.length) {
                    const initialFromList = focusedConversationId ? data.results.find(c => c.id === focusedConversationId) : data.results[0];
                    if (focusedConversationId && !initialFromList) {
                        try {
                            const focused = await DataService.client.url(`/crm/app/conversations/${focusedConversationId}/`).get().json<ConversationDetail>();
                            setSelectedConversationDetail(focused);
                            setConversations(prev => {
                                const exists = prev.some(c => c.id === focused.id);
                                return exists ? prev : [focused as unknown as Conversation, ...prev];
                            });
                        } catch (e) {
                            // Fallback to first item if fetch fails
                            setSelectedConversationDetail(data.results[0] as unknown as ConversationDetail);
                        } finally {
                            setFocusedConversationId(null);
                        }
                    } else {
                        setSelectedConversationDetail((initialFromList || data.results[0]) as unknown as ConversationDetail);
                    }
                }
            })
            .finally(() => {
                setIsLoading(false);
            });

        DataService.client
            .url("/crm/app/conversations/sms_credits/")
            .get()
            .json<SmsCredits>()
            .then(credits => {
                setSmsCredits(credits);
                setIsLoadingSmsCredits(false);
            })
            .catch(err => {
                console.error(err);
                setIsLoadingSmsCredits(false);
                setSmsCredits(null);
            })
            .finally(() => {
                setIsLoadingSmsCredits(false);
            });
    }, [isOpen]);

    // Poll conversations list every 5s when open
    useEffect(() => {
        if (!isOpen) return;
        let isCancelled = false;
        const poll = async () => {
            try {
                // Always refetch all pages up to loadedPagesCount
                let aggregatedResults: Conversation[] = [];
                let page = 1;
                let currentUrl: string | null = "/crm/app/conversations/";
                let lastNext: string | null = null;

                while (currentUrl && page <= Math.max(1, loadedPagesCount)) {
                    // eslint-disable-next-line no-await-in-loop
                    const pageData: PaginatedResponse<Conversation> = await DataService.client.url(currentUrl).get().json<PaginatedResponse<Conversation>>();
                    aggregatedResults = [...aggregatedResults, ...(pageData.results ?? [])];
                    lastNext = pageData?.next ?? null;
                    currentUrl = lastNext;
                    page += 1;
                }

                if (isCancelled) return;

                // Deduplicate by id preserving order
                const seen = new Set<number | string>();
                const uniqueResults: Conversation[] = [];
                for (const item of aggregatedResults) {
                    if (!seen.has(item.id)) {
                        seen.add(item.id);
                        uniqueResults.push(item);
                    }
                }

                // Preserve currently selected conversation if not present
                let nextList: Conversation[] = uniqueResults;
                if (selectedConversationDetail && !nextList.some(c => c.id === selectedConversationDetail.id)) {
                    nextList = [selectedConversationDetail as unknown as Conversation, ...nextList];
                }

                setConversations(nextList);
                setNextUrl(lastNext);
                if (!selectedConversationDetail && nextList.length) {
                    setSelectedConversationDetail(nextList[0]);
                }
            } catch (e) {
                // ignore
                console.error(e);
            }
        };
        const interval = setInterval(poll, 5000);
        return () => {
            isCancelled = true;
            clearInterval(interval);
        };
    }, [isOpen, selectedConversationDetail?.id, loadedPagesCount]);

    useEffect(() => {
        if (!isOpen || !focusedConversationId) return;
        // Clear messages immediately when focused conversation changes
        setMessages([]);
        // Fetch the specific conversation directly instead of searching in conversations array
        DataService.client
            .url(`/crm/app/conversations/${focusedConversationId}/`)
            .get()
            .json<ConversationDetail>()
            .then(conversation => {
                setSelectedConversationDetail(conversation);
                // Inject into list if missing so the sidebar reflects the selection
                setConversations(prev => {
                    const exists = prev.some(c => c.id === conversation.id);
                    return exists ? prev : [conversation as unknown as Conversation, ...prev];
                });
                setFocusedConversationId(null);
            })
            .catch(e => {
                console.error(e);
                setFocusedConversationId(null);
            });
    }, [focusedConversationId, isOpen, selectedConversationDetail?.id]);

    // Removed event-based focus promotion; handled via Sms context

    const loadMore = () => {
        if (!nextUrl || isLoadingMore) return;
        setIsLoadingMore(true);
        DataService.client
            .url(nextUrl)
            .get()
            .json<PaginatedResponse<Conversation>>()
            .then(data => {
                const items = data?.results ?? [];
                // Append while avoiding duplicates
                setConversations(prev => {
                    const existingIds = new Set(prev.map(t => t.id));
                    const merged = [...prev];
                    for (const t of items) {
                        if (!existingIds.has(t.id)) merged.push(t);
                    }
                    return merged;
                });
                setNextUrl(data?.next ?? null);
                setLoadedPagesCount(prev => Math.max(1, prev) + 1);
            })
            .finally(() => {
                setIsLoadingMore(false);
            });
    };

    useEffect(() => {
        if (!isOpen || !selectedConversationDetail) return;
        DataService.client
            .url(`/crm/app/conversations/${selectedConversationDetail.id}/tasks/`)
            .get()
            .json<Task[]>()
            .then(tasks => {
                setMessages(tasks);
                setConversations(prev => {
                    const updatedConversations = prev.map(c => (c.id === selectedConversationDetail.id ? { ...c, seen: true } : c));
                    return updatedConversations;
                })
            }).catch(e => {
                console.error(e);
            });
    }, [isOpen, selectedConversationDetail?.id]);

    // Poll current conversation messages every 5s when open
    useEffect(() => {
        if (!isOpen || !selectedConversationDetail) return;
        let isCancelled = false;
        const poll = async () => {
            try {
                const tasks = await DataService.client.url(`/crm/app/conversations/${selectedConversationDetail.id}/tasks/`).get().json<Task[]>();
                if (isCancelled) return;
                setMessages(tasks);
            } catch (e) {
                // ignore
                console.error(e);
            }
        };
        const interval = setInterval(poll, 5000);
        return () => {
            isCancelled = true;
            clearInterval(interval);
        };
    }, [isOpen, selectedConversationDetail?.id]);

    return (
        <div className="flex h-full w-full overflow-hidden">
            <ConversationsSidebar
                conversations={conversations}
                selectedConversation={selectedConversationDetail}
                onSelect={setSelectedConversationDetail}
                onLoadMore={loadMore}
                hasMore={!!nextUrl}
                isLoading={isLoading}
                smsCredits={smsCredits}
                isLoadingSmsCredits={isLoadingSmsCredits}
            />
            <ConversationPanel
                conversation={selectedConversationDetail}
                smsCredits={smsCredits}
                messages={messages}
                isLoading={isLoading}
                onCall={() => {
                    setIsOpen(false);
                }}
            />
        </div>
    );
}
